<template>
  <div>
    <div v-if="errorMessage" class="error-message">
      <page-error :errorMessage="errorMessage" :errorCode="errorCode"></page-error>
    </div>
    <div id="app" v-else-if="!showEnvelopeComplete && !showPasswordDialog">
      <div class="loader" v-if="isLoading">
        <div class="loader__icon"></div>
      </div>
      <template v-else>
        <app-header :title="$store.getters.title"></app-header>
        <router-view></router-view>
        <modal
          :header="$t('header')"
          :isInitiallyActive="allowTour"
          isLarge="true">
          <envelope-guide></envelope-guide>
          <template slot="modalButtons" slot-scope="props">
            <app-button
              :text="showModalButtons ? $t('functional.noThanks') : $t('functional.close')"
              iconName="times"
              :action="props.close"></app-button>
            <app-button
              :text="$t('functional.showMe')"
              iconName="chevron-right"
              :action="showSlideShow"
              classList="float-right button--cta"
              v-if="showModalButtons"></app-button>
          </template>
        </modal>
      </template>
    </div>
    <div v-else-if="showPasswordDialog">
      <password-protected
        :status="envelopeStatus"
        :clientId="clientId"
        :companyName="companyName"
        :passwordStatus="passwordStatus"
        :passwordIncorrect="passwordStatus.incorrect"
        :requestedEnvelope="requestedEnvelope"
        :getEnvelope="getEnvelope"
        :key="`ps-${passwordStatus.refresh}`"></password-protected>
    </div>
    <div v-else-if="showEnvelopeComplete">
      <envelope-complete
        :status="envelopeStatus"
        :clientId="clientId"
        :companyName="companyName"
        :requestedEnvelope="requestedEnvelope"></envelope-complete>
    </div>
  </div>
</template>

<script>
import AppButton from '@/components/AppButton'
import Header from '@/components/Header'
import Modal from '@/components/Modal'
import EnvelopeGuide from '@/components/EnvelopeGuide'
import EnvelopeComplete from '@/components/EnvelopeComplete'
import PasswordProtected from '@/components/PasswordProtected'
import PageError from '@/components/PageError'
import brandStyler from './utils/brand-styler'
import { envelopeViewed } from './store/api'

export default {
  name: 'app',
  components: {
    'app-header': Header,
    AppButton,
    Modal,
    EnvelopeGuide,
    EnvelopeComplete,
    PasswordProtected,
    PageError
  },
  data () {
    return {
      isLoading: true,
      scrollTop: 0,
      envelopeStatus: null,
      isSigned: false,
      clientId: null,
      companyName: null,
      showModalButtons: true,
      errorMessage: null,
      passwordStatus: {
        refresh: 1,
        required: false,
        title: null,
        password: null
      }
    }
  },
  beforeCreate () {
    this.requestedEnvelope = {
      envelope_fingerprint: this.$route.params.envelopeFingerprint,
      signature_fingerprint: this.$route.params.signatureFingerprint,
      preview_encrypt: this.$route.params.previewURL,
      isPreview: !!this.$route.params.previewURL,
      is_new_encrypt: this.$route.query.new_encrypt,
      widgetCode: this.$route.params.widgetCode
    }

    this.$store.commit('setRequestedEnvelope', this.requestedEnvelope)
    if (this.requestedEnvelope.isPreview) window.IS_PREVIEW = true
  },
  created () {
    this.getEnvelope()

    this.$bus.$on('nextDocument', () => {
      const body = document.getElementsByTagName('body')[0]
      body.scrollTop = 0
      document.documentElement.scrollTop = 0
    })

    this.$bus.$on('loading', () => {
      this.isLoading = true
    })

    this.$bus.$on('loadingStop', () => {
      this.isLoading = false
    })

    this.$bus.$on('submitEnvelope', ({ response }) => {
      if (response.status === 'verify') {
        let redirectUrl = this.$store.getters.widgetRedirectUrl

        if (redirectUrl) {
          if (!redirectUrl.match(/^https:\/\/|^http:\/\//g)) redirectUrl = `http://${redirectUrl}` // Add http:// if not provided
          redirectUrl = new URL(redirectUrl) // Evaluate string as URL object

          window.location.replace(redirectUrl)
        } else {
          this.isSigned = true
          this.envelopeStatus = response.status
        }
      } else {
        this.$store.dispatch('getEnvelope', { query: this.$route.query, password: this.passwordStatus.password }).then((envelope) => {
          this.envelopeStatus = envelope.data.status

          if (response.status === 'rejected') { // TODO This needs to be more generic, isProcessed would be better
            this.isSigned = true
          } else if (response) {
            this.isSigned = response.status === 200
          } else {
            this.isSigned = true
          }

          this.isLoading = false
          if (envelope.data.envelope_redirect_url && response !== null) {
            window.location = envelope.data.envelope_redirect_url
          }
        })
      }
    })
  },
  mounted () {
    if (window.outerWidth <= 575) this.$store.commit('setMobile', { isMobile: true })

    window.addEventListener('scroll', (event) => {
      this.toggleDocumentNav(event)
      this.toggleProgressBar(event)
    })

    this.$bus.$on('scrollBody', () => {
      const header = document.querySelectorAll('header')
      const headerHeight = header[0].firstChild.clientHeight + 70
      const currentId = this.$store.state.activeFieldId
      const input = document.querySelectorAll(`.document-field[data-id="${currentId}"]`)
      if (input.length > 0) {
        const scrollAmount = this.getOffset(input[0])
        this.$SmoothScroll((scrollAmount - headerHeight), 500)
      }
    })
  },
  computed: {
    showPasswordDialog () {
      return this.passwordStatus.required
    },
    allowTour () {
      return parseInt(this.$store.getters.config('company_tour_enable'), 10)
    },
    showEnvelopeComplete () {
      return this.isSigned || ['cancelled', 'rejected', 'signed', 'expired'].indexOf(this.envelopeStatus) > -1
    }
  },
  methods: {
    getEnvelope (password) {
      this.$store.dispatch('getEnvelope', { query: this.$route.query, password }).then((response) => {
        const data = response.data
        this.passwordStatus.required = false

        if (!this.$route.params.widgetCode) {
          this.isSigned = data.isSigned
          this.clientId = data.client.client_id
          this.envelopeStatus = data.status
          this.companyName = data.client.company_name
        } else {
          this.clientId = data.config.company_id
          this.companyName = data.config.company_name
        }

        if (!this.isSigned) {
          this.isLoading = false
          if (!this.$route.params.widgetCode) envelopeViewed(this.requestedEnvelope)
        } else {
          this.isLoading = false
        }

        if (password) this.passwordStatus.password = password

        brandStyler(data.colours)
      }).catch((error) => {
        this.isLoading = false

        if (error.response.status === 401) {
          this.passwordStatus.required = true
          this.passwordStatus.title = error.response.data.title
          this.passwordStatus.incorrect = !!password
          this.passwordStatus.refresh++ // TODO Fix reactvitity issues in a better way
        } else {
          this.errorMessage = error.response.data.message
          this.errorCode = error.response.status
        }
      })
    },
    getOffset (el) {
      const element = el.getBoundingClientRect()
      return element.top + (window.scrollY || window.pageYOffset)
    },
    toggleDocumentNav () {
      const windowScrollHeight = window.scrollTop || window.pageYOffset
      if ((windowScrollHeight + window.innerHeight) >= document.body.offsetHeight) {
        this.$bus.$emit('scrolledToBottom', { isBottom: true })
      } else {
        this.$bus.$emit('scrolledToBottom', { isBottom: false })
      }
    },
    toggleProgressBar () {
      const scrollTop = window.scrollTop || window.pageYOffset
      if (scrollTop > this.scrollTop + 20) {
        this.$bus.$emit('scrollingDown')
      } else if (scrollTop < (this.scrollTop - 20)) {
        this.$bus.$emit('scrollingUp')
      }
      if (scrollTop === 0) {
        this.$bus.$emit('scrollingUp')
      }
      this.scrollTop = scrollTop
    },
    showSlideShow () {
      this.$bus.$emit('showSlideShow')
      this.showModalButtons = false
    }
  }
}
</script>

<style lang="sass">
  @import '@/styles/manifest.sass'
  #app
    position: relative

  .error-message
    text-align: center;
</style>
